import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Camera, RotateCcw, X, Loader2, KeyRound, CheckCircle2, AlertTriangle, ImagePlus } from 'lucide-react';
import { ProductImagePanel } from '@/components/audit/ProductImagePanel';

export const getDeviceId = () => {
  let id = localStorage.getItem('audit_device_id');
  if (!id) { id = `dev_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`; localStorage.setItem('audit_device_id', id); }
  return id;
};
export const deviceInfo = () => `${navigator.platform || ''} · ${navigator.userAgent.slice(0, 90)} · ${window.screen.width}x${window.screen.height}`;

export const PinGate = ({ info, onLogin, busy, error }) => {
  const [pin, setPin] = useState('');
  return (
    <div className="max-w-sm mx-auto bg-white rounded-2xl shadow-lg p-6 space-y-4" data-testid="public-pin-gate">
      <div className="text-center">
        <KeyRound className="w-10 h-10 mx-auto text-emerald-600" />
        <h1 className="text-xl font-bold mt-2">{info?.title || 'Stock audit'}</h1>
        <p className="text-sm text-gray-500">{info?.audit_number} · {info?.location_name}</p>
        {info?.filters_label && <p className="text-xs text-gray-400 mt-1">{info.filters_label}</p>}
      </div>
      <Input inputMode="numeric" autoFocus value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))} placeholder="Enter your PIN"
        className="text-center text-2xl tracking-[0.5em] h-14" onKeyDown={(e) => e.key === 'Enter' && pin.length >= 4 && onLogin(pin)} data-testid="public-pin-input" />
      {error && <p className="text-sm text-red-600 text-center" data-testid="public-pin-error">{error}</p>}
      <Button className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-base" disabled={busy || pin.length < 4} onClick={() => onLogin(pin)} data-testid="public-pin-submit">
        {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Start counting
      </Button>
      <p className="text-[11px] text-gray-400 text-center">Ask your store manager for your personal PIN.</p>
    </div>
  );
};

// Captures a downscaled JPEG selfie from the front camera (falls back to file input).
export const SelfieOnboarding = ({ staffName, onDone, busy, error }) => {
  const [name, setName] = useState(staffName || '');
  const [selfie, setSelfie] = useState('');
  const [camErr, setCamErr] = useState('');
  const videoRef = useRef(null); const streamRef = useRef(null);

  const start = async () => {
    setCamErr('');
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 640 } }, audio: false });
      streamRef.current = s; if (videoRef.current) { videoRef.current.srcObject = s; await videoRef.current.play(); }
    } catch (e) { setCamErr('Camera not available — use the upload button below.'); }
  };
  const stop = () => { streamRef.current?.getTracks().forEach(t => t.stop()); streamRef.current = null; };
  useEffect(() => { start(); return stop; }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const capture = () => {
    const v = videoRef.current; if (!v || !v.videoWidth) return;
    const c = document.createElement('canvas'); const w = 480; c.width = w; c.height = Math.round(v.videoHeight * (w / v.videoWidth));
    c.getContext('2d').drawImage(v, 0, 0, c.width, c.height);
    setSelfie(c.toDataURL('image/jpeg', 0.7)); stop();
  };
  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const img = new Image(); const url = URL.createObjectURL(f);
    img.onload = () => { const c = document.createElement('canvas'); const w = 480; c.width = w; c.height = Math.round(img.height * (w / img.width)); c.getContext('2d').drawImage(img, 0, 0, c.width, c.height); setSelfie(c.toDataURL('image/jpeg', 0.7)); URL.revokeObjectURL(url); };
    img.src = url;
  };

  return (
    <div className="max-w-sm mx-auto bg-white rounded-2xl shadow-lg p-6 space-y-4" data-testid="public-onboarding">
      <h1 className="text-lg font-bold">Before you start</h1>
      <p className="text-sm text-gray-500">Confirm your name and take a selfie. Your device is registered so every count you enter can be verified.</p>
      <div><label className="text-xs text-gray-600">Your name</label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="public-onboard-name" /></div>
      <div className="rounded-xl overflow-hidden bg-black aspect-[4/3] relative">
        {selfie ? <img src={selfie} alt="selfie" className="w-full h-full object-cover" data-testid="public-selfie-preview" />
          : <video ref={videoRef} playsInline muted className="w-full h-full object-cover" />}
        {camErr && !selfie && <div className="absolute inset-0 flex items-center justify-center text-white text-sm p-4 text-center">{camErr}</div>}
      </div>
      <div className="flex gap-2">
        {selfie ? <Button variant="outline" className="flex-1" onClick={() => { setSelfie(''); start(); }}><RotateCcw className="w-4 h-4 mr-1" /> Retake</Button>
          : <Button className="flex-1 bg-gray-900" onClick={capture} disabled={!!camErr} data-testid="public-selfie-capture"><Camera className="w-4 h-4 mr-1" /> Take selfie</Button>}
        <label className="flex-1"><input type="file" accept="image/*" capture="user" className="hidden" onChange={onFile} data-testid="public-selfie-file" /><span className="inline-flex w-full h-10 items-center justify-center rounded-md border text-sm cursor-pointer hover:bg-gray-50">Upload photo</span></label>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <Button className="w-full h-12 bg-emerald-600 hover:bg-emerald-700" disabled={busy || !selfie || name.trim().length < 2} onClick={() => onDone({ name: name.trim(), selfie })} data-testid="public-onboard-submit">
        {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Continue to counting
      </Button>
    </div>
  );
};

// Rear-camera barcode / QR scanner (html5-qrcode). Calls onCode(text) once per detection.
export const CameraScanner = ({ onCode, onClose }) => {
  const [err, setErr] = useState('');
  const lastRef = useRef({ code: '', at: 0 });
  useEffect(() => {
    const scanner = new Html5Qrcode('audit-camera-scanner', { verbose: false });
    scanner.start({ facingMode: 'environment' }, { fps: 10, qrbox: { width: 260, height: 160 } }, (text) => {
      const now = Date.now();
      if (text === lastRef.current.code && now - lastRef.current.at < 2500) return;
      lastRef.current = { code: text, at: now };
      if (navigator.vibrate) navigator.vibrate(60);
      onCode(text);
    }, () => {}).catch(() => setErr('Could not open the camera. Allow camera access or type the barcode.'));
    return () => {
      // html5-qrcode throws synchronously from stop() if the camera never started (no permission / no camera)
      try { scanner.stop().then(() => scanner.clear()).catch(() => {}); } catch (_) { try { scanner.clear(); } catch (__) { /* noop */ } }
    };
  }, [onCode]);
  return (
    <div className="rounded-xl overflow-hidden bg-black relative" data-testid="public-camera-scanner">
      <div id="audit-camera-scanner" className="w-full" />
      {err && <p className="text-white text-sm p-4 text-center">{err}</p>}
      <button onClick={onClose} className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5" aria-label="Close scanner" data-testid="public-scanner-close"><X className="w-4 h-4" /></button>
    </div>
  );
};

export const ProductCountCard = ({ line, reasons, onSubmit, onCancel, busy, imageApi, onImageSaved }) => {
  const [qty, setQty] = useState(''); const [reason, setReason] = useState(''); const [remarks, setRemarks] = useState(''); const [force, setForce] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  useEffect(() => { setQty(''); setReason(''); setRemarks(''); setForce(false); setPhotoOpen(false); }, [line?.product_id]);
  if (!line) return null;
  const q = qty === '' ? null : parseFloat(qty);
  const verified = line.physical_qty !== null && line.physical_qty !== undefined;
  // new entry compares with LIVE stock (what the server will use); an untouched verified line shows its stored variance
  const base = line.current_qty ?? line.system_qty;
  const v = q === null || Number.isNaN(q) ? (verified ? line.variance : null) : +(q - base).toFixed(3);
  const needReason = v !== null && v !== 0 && !reason && q !== null;
  return (
    <div className="bg-white rounded-2xl shadow-lg p-4 space-y-3" data-testid="public-count-card">
      <div className="flex gap-3">
        <button type="button" onClick={() => imageApi && setPhotoOpen(o => !o)} className="relative shrink-0" aria-label="Product photo" data-testid="public-photo-toggle">
          {line.image_url ? <img src={line.image_url} alt="" className="w-16 h-16 rounded-lg object-cover border" data-testid="public-line-image" />
            : <div className="w-16 h-16 rounded-lg border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-gray-400"><ImagePlus className="w-5 h-5" /></div>}
          {imageApi && <span className="absolute -bottom-1 -right-1 rounded-full bg-indigo-600 text-white p-1"><Camera className="w-3 h-3" /></span>}
        </button>
        <div className="min-w-0 flex-1">
          <div className="font-semibold leading-tight" data-testid="public-count-product">{line.product_name}</div>
          <div className="text-xs text-gray-500">{line.brand_name ? `${line.brand_name} · ` : ''}{line.sku}{line.barcode ? ` · ${line.barcode}` : ''}</div>
          {line.vendor_names?.length ? <div className="text-[11px] text-gray-400 truncate">Supplier: {line.vendor_names.join(', ')}</div> : null}
          {line.image_updated_by ? <div className="text-[10px] text-indigo-600">Photo by {line.image_updated_by}</div> : null}
        </div>
        <button onClick={onCancel} className="text-gray-400" aria-label="Close count card" data-testid="public-count-close"><X className="w-5 h-5" /></button>
      </div>
      {photoOpen && imageApi && <ProductImagePanel line={line} api={imageApi} onSaved={(r) => { onImageSaved?.(r); }} onClose={() => setPhotoOpen(false)} />}
      {verified && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm" data-testid="public-already-verified">
          <div className="flex items-center gap-1 font-medium text-amber-800"><CheckCircle2 className="w-4 h-4" /> Already verified</div>
          <div className="text-amber-700 text-xs mt-0.5">Counted <b>{line.physical_qty}</b> by <b>{line.counted_by}</b> on {new Date(line.counted_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}{line.reason ? ` · ${line.reason}` : ''}</div>
          <label className="flex items-center gap-2 mt-2 text-xs text-amber-900"><input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} data-testid="public-recount-check" /> I need to recount this product (logged as a recount)</label>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-gray-100 p-3 text-center"><div className="text-[11px] text-gray-500 uppercase">{verified ? 'In system at count' : 'In system now'}</div><div className="text-2xl font-bold" data-testid="public-system-qty">{line.system_qty}</div></div>
        <div className={`rounded-xl p-3 text-center ${v === null ? 'bg-gray-100' : v === 0 ? 'bg-emerald-100' : 'bg-red-100'}`}><div className="text-[11px] text-gray-500 uppercase">Variance</div>
          <div className={`text-2xl font-bold ${v === null ? 'text-gray-400' : v === 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="public-variance">{v === null ? '—' : v > 0 ? `+${v}` : v}</div></div>
      </div>
      {verified && line.moved_since_count ? (
        <div className="rounded-xl border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900 flex items-center justify-between" data-testid="public-rollforward">
          <span>{line.moved_since_count < 0 ? `${-line.moved_since_count} sold / moved out` : `${line.moved_since_count} received`} after your count · stock now {line.current_qty}</span>
          <span className="font-bold text-sm">Final stock {line.final_qty}</span>
        </div>
      ) : null}
      <div>
        <label className="text-xs text-gray-600">Physical stock available</label>
        <Input type="number" inputMode="decimal" step="any" min="0" autoFocus value={qty} onChange={(e) => setQty(e.target.value)} disabled={verified && !force}
          className="h-14 text-2xl text-center" placeholder="0" onKeyDown={(e) => e.key === 'Enter' && !needReason && q !== null && onSubmit({ physical_qty: q, reason: reason || undefined, remarks: remarks || undefined, force })} data-testid="public-phys-input" />
      </div>
      {q !== null && v !== null && v !== 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-1 text-xs text-red-700"><AlertTriangle className="w-3.5 h-3.5" /> Variance — pick a reason (required)</div>
          <div className="flex flex-wrap gap-1.5">
            {reasons.map(r => <button key={r} type="button" onClick={() => setReason(r)} className={`px-2.5 py-1.5 rounded-full text-xs border ${reason === r ? 'bg-red-600 text-white border-red-600' : 'bg-white border-gray-300'}`} data-testid={`public-reason-${r.replace(/[^a-z]/gi, '').toLowerCase()}`}>{r}</button>)}
          </div>
          <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Remarks (optional)" data-testid="public-remarks" />
        </div>
      )}
      <Button className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-base" disabled={busy || q === null || Number.isNaN(q) || needReason || (verified && !force)}
        onClick={() => onSubmit({ physical_qty: q, reason: reason || undefined, remarks: remarks || undefined, force })} data-testid="public-count-submit">
        {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{verified ? 'Save recount' : 'Save count'}
      </Button>
    </div>
  );
};
